import React, { useState, useEffect, useRef } from 'react'
import Navber from '../Components/Navber.jsx'
import '../Styles/Group_tour.css'
import '../Styles/Package_tour.css'
import Common_tour_card from '../Components/Common_tour_card.jsx'
import Typemsg from '../Components/Typemsg.jsx'
import User_Destinations from '../Components/User_Destinations.jsx'
import api from '../Api.js'
import TourCardSkeleton from "../Components/Helping Component/TourCardSkeleton.jsx"
import TourFilters from "../Components/TourFilters.jsx"

// ✅ Helper to safely get the ID regardless of API structure
const getItemId = (item) => {
  return item?.id || item?.tour?.id || item?.tour?._id || item?._id;
};

const sortTours = (data, option) => {
  const sortedData = [...data];

  switch (option) {
    case "price_low_high":
      return sortedData.sort(
        (a, b) =>
          (a.tour?.pricing?.per_person?.adult || 0) -
          (b.tour?.pricing?.per_person?.adult || 0)
      );

    case "price_high_low":
      return sortedData.sort(
        (a, b) =>
          (b.tour?.pricing?.per_person?.adult || 0) -
          (a.tour?.pricing?.per_person?.adult || 0)
      );

    case "duration":
      return sortedData.sort(
        (a, b) => (a.tour?.no_of_days || 0) - (b.tour?.no_of_days || 0)
      );

    case "rating":
      return sortedData.sort(
        (a, b) => (b.rating || 0) - (a.rating || 0)
      );

    default:
      return sortedData;
  }
};

function PlanTour() {
  const [sortOption, setSortOption] = useState("popularity")
  const [searchText, setSearchText] = useState("")
  const [isLoading, setIsLoading] = useState(true) // Initial load
  const [isFetchingMore, setIsFetchingMore] = useState(false) // Loading more items at bottom
  const [filteredItems, setFilteredItems] = useState([])
  const [packageData, setPackageData] = useState([])
  const [searchResult, setSearchResult] = useState("Search Results...")
  const [mobilePanel, setMobilePanel] = useState(null)

  // Pagination States
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const LIMIT = 6; // Limit per page matching the original itemsPerPage

  // Refs for Infinite Scrolling & Locking
  const observerTarget = useRef(null);
  const isFetchingRef = useRef(false);
  const packageDataRef = useRef([]);

  // Keep the ref updated with the latest state
  useEffect(() => {
    packageDataRef.current = packageData;
  }, [packageData]);

  // ✅ Unified Fetch Function (Handles Initial Load & Pagination)
  const fetchPackages = async (currentPage, isInitial = false) => {
    if (isFetchingRef.current) return; // Block if already fetching
    isFetchingRef.current = true;

    if (isInitial) setIsLoading(true);
    else setIsFetchingMore(true);

    try {
      // NOTE: Ensure your backend accepts page and limit query parameters
      const res = await api.get(`/tours/details?trip_type_id=1&page=${currentPage}&limit=${LIMIT}`)
      
      console.log(`Plan Tour API Response for page ${currentPage}:`, res?.data);

      if (res?.data?.success === true) {
        const newData = Array.isArray(res?.data?.data) ? res.data.data : [];

        // If API returns an empty array, we truly reached the end.
        if (newData.length === 0) {
          setHasMore(false);
        } else {
          // Check for duplicates using the safe ID helper
          const existingIds = new Set(packageDataRef.current.map(getItemId));
          const uniqueNewData = newData.filter((item) => !existingIds.has(getItemId(item)));

          // If all returned items are duplicates, stop pagination to prevent infinite loops
          if (uniqueNewData.length === 0 && newData.length > 0 && !isInitial) {
            setHasMore(false);
          }

          if (isInitial) {
            setPackageData(newData);
          } else {
            if (uniqueNewData.length > 0) {
              setPackageData((prev) => [...prev, ...uniqueNewData]);
            }
          }
        }
      }
    } catch (error) {
      console.error("Plan tour fetch error:", error)
      setHasMore(false); // Stop fetching on error
    } finally {
      isFetchingRef.current = false; // Unlock
      setIsLoading(false);
      setIsFetchingMore(false);
    }
  };

  // ✅ Initial Fetch
  useEffect(() => {
    fetchPackages(1, true);
  }, []);

  // ✅ Fetch when page changes (triggered by scroll)
  useEffect(() => {
    if (page > 1) {
      fetchPackages(page, false);
    }
  }, [page]);

  // ✅ Intersection Observer for Infinite Scroll
  useEffect(() => {
    const target = observerTarget.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetchingRef.current && !isLoading) {
          setPage((prevPage) => prevPage + 1);
        }
      },
      // rootMargin helps trigger the fetch slightly before the user hits the absolute bottom
      { rootMargin: "150px", threshold: 0.1 }
    );

    observer.observe(target);

    return () => {
      observer.unobserve(target);
    };
  }, [hasMore, isLoading]);

  const handleSortChange = (event) => {
    setSortOption(event.target.value)
    setMobilePanel(null)
  };

  const handleSearch = (event) => {
    event?.preventDefault()
  }

  // Filtering and Sorting Logic
  useEffect(() => {
    const query = searchText.trim().toLowerCase()

    let data = packageData.filter((item) => {
      if (!query) {
        return true
      }

      const destinationName =
        item.tour?.destination?.name?.toLowerCase() || ""

      const destinationSlug =
        item.tour?.destination?.slug?.toLowerCase() || ""

      return (
        item.tour?.description?.toLowerCase().includes(query) ||
        item.tour?.title?.toLowerCase().includes(query) ||
        destinationName.includes(query) ||
        destinationSlug.includes(query)
      )
    })

    data = sortTours(data, sortOption)
    setFilteredItems(data)

    setSearchResult(
      query && data.length === 0
        ? "No Search Result"
        : query
          ? `Found ${data.length} result(s)`
          : ""
    )
  }, [searchText, packageData, sortOption])

  const sortOptions = [
    { key: "popularity", label: "Popularity" },
    { key: "price_low_high", label: "Price: Low → High" },
    { key: "price_high_low", label: "Price: High → Low" },
    { key: "duration", label: "Duration (Days)" },
    { key: "rating", label: "Rating" },
  ]

  return (
    <div style={{ minHeight: "100vh" }}>
      <div style={{ backgroundColor: "#f0f0f0" }}>
        <Navber />
      </div>

      <div
        className='group_container'
        style={{
          background: `url(${require("../Images/customtour.jpg")}) center/cover no-repeat`
        }}
      >
        <h1
          style={{
            color: 'white',
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            padding: '10px 20px',
            borderRadius: '8px',
            display: 'inline-block',
            backdropFilter: 'blur(4px)'
          }}
        >
          Plan Tour
        </h1>
      </div>

      <div className="tourPageLayout">
        <TourFilters
          mobilePanel={mobilePanel}
          setMobilePanel={setMobilePanel}
          searchText={searchText}
          setSearchText={setSearchText}
          handleSearch={handleSearch}
          searchResult={searchResult}
          sortOption={sortOption}
          handleSortChange={handleSortChange}
          sortOptions={sortOptions}
        />

        <div className="cardsSection">
          {isLoading ? (
            <div className="tourCardsGrid">
              {Array.from(new Array(LIMIT)).map((_, index) => (
                <TourCardSkeleton key={index} />
              ))}
            </div>
          ) : (
            <>
              {filteredItems.length === 0 ? (
                <h6 style={{ textAlign: "center", marginTop: "20px", width: "100%" }}>
                  No tours found.
                </h6>
              ) : (
                <div className="tourCardsGrid">
                  {/* ✅ Render ALL filtered items. Infinite scroll handles fetching more. */}
                  {filteredItems.map((item) => (
                    <Common_tour_card key={getItemId(item)} packagedata={item} />
                  ))}
                </div>
              )}

              {/* 🔄 Loading Skeleton for Fetching More */}
              {isFetchingMore && (
                <div className="tourCardsGrid" style={{ width: "100%", marginTop: "20px" }}>
                  {Array.from(new Array(LIMIT)).map((_, index) => (
                    <TourCardSkeleton key={`fetching-skeleton-${index}`} />
                  ))}
                </div>
              )}

              {/* 🔄 Infinite Scroll Sentinel */}
              {hasMore && !isFetchingMore && (
                <div ref={observerTarget} style={{ height: "20px", width: "100%", marginTop: "20px" }} />
              )}
            </>
          )}
        </div>
      </div>

      <div className="mobileTourControls">
        <button type="button" onClick={() => setMobilePanel("filter")}>
          ☰ Filter
        </button>

        <button type="button" onClick={() => setMobilePanel("sort")}>
          ⇅ Sort
        </button>
      </div>

      <User_Destinations />
      {/* <Typemsg /> */}
    </div>
  )
}

export default PlanTour